import { useMemo } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import set from 'lodash/set';
import { ArrayField, FunctionField, RecordContextProvider, TextField, useRecordContext } from 'react-admin';
import { Link } from '@mui/material';
import { SecondaryDatagridStyled } from '@/styles/overrides';
import { formatDate } from '@/pages/components/SummaryDetails/utils';
import { OneOffChecksTableProps } from '@/pages/Candidate/view/tables/OneOffChecksTable/types';
import type { OneOffCheckReport } from '@/pages/Candidate/view/tables/OneOffChecksTable/types';
import OneOffCheckReportExpand from '@/pages/Candidate/view/tables/OneOffChecksTable/OneOffCheckReportExpand';
import { sortOneOffChecksByCreatedAtDesc } from '@/pages/Candidate/view/tables/OneOffChecksTable/sortOneOffChecksByCreatedAtDesc';
import { DatagridSx, ReportNameContainer } from './styled';

const isRowExpandable = (record: OneOffCheckReport) =>
  Array.isArray(record?.custom_fields) && record.custom_fields.length > 0;

const OneOffChecksTable = ({ source }: OneOffChecksTableProps) => {
  const record = useRecordContext();
  const reports = (get(record, source) ?? []) as OneOffCheckReport[];
  const anyRowHasCustomFields = reports.some((r) => Array.isArray(r?.custom_fields) && r.custom_fields.length > 0);

  const sortedRecord = useMemo(() => {
    if (record == null) return null;
    const base = cloneDeep(record) as Record<string, unknown>;
    const raw = ((get(base, source) as OneOffCheckReport[] | undefined) ?? []) as OneOffCheckReport[];
    set(base, source, sortOneOffChecksByCreatedAtDesc(raw));
    return base;
  }, [record, source]);

  const grid = (
    <ArrayField source={source}>
      <SecondaryDatagridStyled
        bulkActionButtons={false}
        rowClick={false}
        sx={DatagridSx}
        {...(anyRowHasCustomFields ? { expand: <OneOffCheckReportExpand />, isRowExpandable } : {})}
      >
        <FunctionField
          label="Name"
          sortable={false}
          render={(row: OneOffCheckReport) => {
            const connectReport = row?.connect_order_report;
            return (
              <ReportNameContainer>
                <span>{row?.name || row?.description || '—'}</span>
                {connectReport ? <small>Connect Order ID: {connectReport.connect_order_id}</small> : null}
              </ReportNameContainer>
            );
          }}
        />
        <TextField source="state" label="State" sortable={false} />
        <TextField source="resolution" label="Resolution" sortable={false} />
        <TextField source="source" label="Recheck ID" sortable={false} />
        <FunctionField
          label="Created At"
          sortable={false}
          render={(row: OneOffCheckReport) => formatDate(row?.created_at) || '—'}
        />
        <FunctionField
          label="Report"
          sortable={false}
          render={(row: OneOffCheckReport) =>
            row?.report_url ? (
              <Link href={row.report_url} target="_blank" rel="noopener noreferrer">
                View
              </Link>
            ) : (
              '—'
            )
          }
        />
      </SecondaryDatagridStyled>
    </ArrayField>
  );

  if (sortedRecord == null) {
    return grid;
  }

  return <RecordContextProvider value={sortedRecord}>{grid}</RecordContextProvider>;
};

export default OneOffChecksTable;
